import {
  CategoryCoverageDto,
  CategoryResponseDto,
} from 'enrollment/queries/useBenefitCategoryQuery/types';
import { getPremiumDisplay } from 'enrollment/features/Benefits/BenefitsPreview/GroupedBenefits/utils';
import { Plan } from 'enrollment/features/Review/components';
import { UnselectedBenefitV2 } from './UnselectedBenefitV2';

interface GroupedBenefitsProps {
  benefitCategoryData: CategoryResponseDto;
  benefitCategory: string;
  currentBenefitCategory: string;
  color: string;
  loading?: boolean;
  onBenefitMount?: (benefitId: string) => void;
  bundledBenefitOverride?: boolean; // this is used to override base logic for savvi bundled benefits
}

export const GroupedBenefitsV2 = ({
  benefitCategoryData,
  benefitCategory,
  currentBenefitCategory,
  color,
  loading,
  onBenefitMount,
  bundledBenefitOverride = false, // this is used to override base logic for savvi bundled benefits
}: GroupedBenefitsProps) => {
  const contextLookup = benefitCategoryData.data.context_lookup_code;

  // distinguish if the coverage is terminated not from being declined
  const isNonDeclinedTermination = (coverage: CategoryCoverageDto) => {
    const startDate = new Date(coverage.change_effective_date || '');
    const terminationDate = new Date(coverage.termination_date || '');

    if (!terminationDate || !startDate) {
      return false;
    }
    if (
      coverage.req_coverage_level_lookup_code === 'decline' ||
      coverage.coverage_level_lookup_code === 'decline'
    ) {
      return false;
    }
    if (terminationDate <= new Date() || startDate >= terminationDate) {
      return true;
    }

    return false;
  };

  // all of the benefits filter to only a specific category and get the benefit tiles
  return benefitCategoryData.data.benefits.map((core, subIndex) => {
    const params = {
      benefitData: core,
      pendedBenefitData: benefitCategoryData.data.pended_benefits
        ? benefitCategoryData.data.pended_benefits[subIndex]
        : undefined,
      category: benefitCategory,
      currentCategory: currentBenefitCategory,
      iconColor: color,
      loading,
      contextLookup,
      planRequiresConfirmation:
        core.coverage !== null &&
        core.benefit.active_enrollment_required &&
        !benefitCategoryData.data.active_enrollment_benefit_status[
          core.benefit.org_benefit_id.toString()
        ],
      // navigatePath: isContributionBenefit(core.benefit.benefit_class)
      //   ? '/enrollment/financial/plans'
      //   : undefined,
    };

    const isSurveyNoQuestions =
      core.benefit.benefit_class === 'survey' &&
      Number(core.org_benefit_survey_questions?.length) < 1;

    const hideLine =
      isSurveyNoQuestions ||
      (!bundledBenefitOverride &&
        core.benefit.org_benefit_category !== benefitCategory);

    if (hideLine) {
      return null;
    }

    if (!core.coverage || isNonDeclinedTermination(core.coverage)) {
      return (
        <UnselectedBenefitV2
          key={`${benefitCategory}-${subIndex}`}
          {...params}
          onMount={onBenefitMount}
        />
      );
    } else {
      return (
        <Plan
          key={core.benefit.org_benefit_name}
          onMount={onBenefitMount}
          planName={
            core.coverage?.req_coverage_level_lookup_code?.toLowerCase() ===
            'decline'
              ? core.benefit.org_benefit_name
              : core.coverage?.req_org_plan_name ||
                core.benefit.org_benefit_name
          }
          inEligible={isNonDeclinedTermination(
            core.coverage as CategoryCoverageDto
          )}
          cost={core.coverage ? `${getPremiumDisplay(core.coverage)}` : ''}
          subscriberCost={core.coverage?.subscriber_premium || ''}
          declined={
            core.coverage?.req_coverage_level_lookup_code === 'decline'
              ? true
              : false
          }
          planId={String(core.coverage?.req_org_plan_id)}
          coverageAmt={core.coverage?.req_volume}
          guaranteedAmount={core.coverage?.volume}
          provider={String(core.coverage?.req_carrier_name)}
          coveredIndividuals={core.coverage?.req_dependents}
          isDependentOnlyPlan={core.coverage?.is_dependent_only_plan || false}
          contextLookup={contextLookup}
          benefitData={core}
          orgBenefitId={core.benefit.org_benefit_id}
          benefitCategory={benefitCategory}
          benefitClass={core.benefit.benefit_class}
          waitingForEoi={core.coverage?.waiting_for_eoi}
          waitingForDocRequest={core.coverage?.waiting_for_doc_requests}
          // canEdit={core.coverage ? core.plans?.length > 0 : false}
          currentCategory={currentBenefitCategory}
          planRequiresConfirmation={
            core.coverage !== null &&
            core.benefit.active_enrollment_required &&
            !benefitCategoryData.data.active_enrollment_benefit_status[
              core.benefit.org_benefit_id.toString()
            ]
          }
        />
      );
    }
  });
};
